import type { ReactNode } from 'react';
import type { Capability } from '@rundown/policy';
import { useCapability } from './useCapability';

export interface RequireCapabilityProps {
  readonly capability: Capability;
  readonly children: ReactNode;
  /** Shown instead of nothing — use sparingly; absence is usually quieter than an explanation. */
  readonly fallback?: ReactNode;
}

/**
 * Renders `children` only when the current role holds `capability`.
 *
 * Read the warning at the top of useCapability.ts first: this hides UI, it does
 * not protect anything. The server rejects the call regardless of what rendered.
 *
 * Nothing at all is rendered when the capability is absent — no empty wrapper, no
 * "you don't have access" notice. A client who cannot raise a change of plan
 * should see a screen that looks complete, not one full of locked doors.
 */
export function RequireCapability({ capability, children, fallback = null }: RequireCapabilityProps) {
  return useCapability(capability) ? <>{children}</> : <>{fallback}</>;
}
